import React, {useState, useEffect, useRef, useCallback} from "react";
import { useLanguage } from '../../../hooks/useLanguage';
import SubBanner from "../../../components/SubBanner";
import bannerImg from "../../../assets/img/about_bg.png";

function Location(){
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState(1);
    const mapElement = useRef(null);
    const mapInstance = useRef(null);
    const markerInstance = useRef(null);
    const infowindowInstance = useRef(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [mapError, setMapError] = useState(null);
    const naverKey = "ugyda2zpxz";  
    
    // 배너 탭
    const tabLabels = t('company.tab', []);
    const tabList = [
        { label: tabLabels[0], url: '/company/about' },
        { label: tabLabels[1], url: '/company/location' },
        { label: tabLabels[2], url: 'https://careers.nhn.com/' }
    ];

    // 주소 정보 다국어 처리
    const locationInfo = t("sub_section_1.location_info", []);

    // 네이버 지도 인증 실패 처리
    useEffect(() => {
        window.navermap_authFailure = function() {
            setMapError("네이버 지도 인증에 실패했습니다. 클라이언트 ID를 확인해주세요.");
            console.error("네이버 지도 인증 실패");
        };

        return () => {
            window.navermap_authFailure = null;
        };
    }, []);

    // 네이버 지도 스크립트 로드
    const loadNaverMapsScript = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (window.naver && window.naver.maps) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.id = 'naver-maps-script';
            // 새로운 API URL 형식 사용
            script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${naverKey}`;
            script.async = true;

            script.onload = () => {
                const checkNaverMaps = setInterval(() => {
                    if (window.naver && window.naver.maps) {
                        clearInterval(checkNaverMaps);
                        setIsMapLoaded(true);
                        resolve();
                    }
                }, 100);

                // 10초 후에도 로드되지 않으면 타임아웃 처리
                setTimeout(() => {
                    clearInterval(checkNaverMaps);
                    if (!window.naver || !window.naver.maps) {
                        setMapError("지도 로드 시간이 초과되었습니다.");
                        reject(new Error("Map load timeout"));
                    }
                }, 10000);
            };
            
            script.onerror = (error) => {
                setMapError("지도 스크립트 로드에 실패했습니다.");
                console.error('네이버 지도 스크립트 로드 실패:', error);
                reject(error);
            };

            document.head.appendChild(script);
        });
    }, [naverKey]);

    // 지도 초기화
    const initMap = useCallback(async () => {
        if (!mapElement.current || !isMapLoaded) return;

        try {
            // 이전 인스턴스 정리
            if (infowindowInstance.current) {
                infowindowInstance.current.close();
            }
            
            if (markerInstance.current) {
                markerInstance.current.setMap(null);
            }

            if (mapInstance.current) {
                mapInstance.current.destroy();
            }

            // NHN ACE 플레이뮤지엄의 정확한 좌표로 수정
            const position = new window.naver.maps.LatLng(37.4011, 127.1042);
            
            const mapOptions = {
                center: position,
                zoom: 16,
                zoomControl: true,
                zoomControlOptions: {
                    position: window.naver.maps.Position.TOP_RIGHT
                }
            };

            const map = new window.naver.maps.Map(mapElement.current, mapOptions);
            mapInstance.current = map;

            const marker = new window.naver.maps.Marker({
                position: position,
                map: map
            });
            markerInstance.current = marker;

            const contentString = [
                '<div class="iw_inner" style="padding: 10px;">',
                '   <h3 style="font-size: 14px; margin-bottom: 5px;">NHN ACE</h3>',
                '   <p style="font-size: 12px;">경기도 성남시 분당구 대왕판교로 645번길 16 플레이뮤지엄</p>',
                '</div>'
            ].join('');

            const infowindow = new window.naver.maps.InfoWindow({
                content: contentString,
                maxWidth: 300,
                backgroundColor: "#fff",
                borderColor: "#888",
                borderWidth: 2,
                anchorSize: new window.naver.maps.Size(30, 30),
                anchorSkew: true,
                anchorColor: "#fff",
                pixelOffset: new window.naver.maps.Point(20, -20)
            });
            infowindowInstance.current = infowindow;

            // 마커 클릭 이벤트
            window.naver.maps.Event.addListener(marker, 'click', () => {
                if (infowindow.getMap()) {
                    infowindow.close();
                } else {
                    infowindow.open(map, marker);
                }
            });

        } catch (error) {
            setMapError("지도 초기화 중 오류가 발생했습니다.");
            console.error('지도 초기화 실패:', error);
        }
    }, [isMapLoaded]);

    // 스크립트 로드
    useEffect(() => {
        loadNaverMapsScript();
    }, [loadNaverMapsScript]);

    // 지도 초기화
    useEffect(() => {
        if (isMapLoaded) {
            initMap();
        }
    }, [isMapLoaded, initMap]);

    // 클린업
    useEffect(() => {
        return () => {
            if (infowindowInstance.current) {
                infowindowInstance.current.close();
                infowindowInstance.current = null;
            }
            
            if (markerInstance.current) {
                markerInstance.current.setMap(null);
                markerInstance.current = null;
            }

            if (mapInstance.current) {
                mapInstance.current.destroy();
                mapInstance.current = null;
            }

            const script = document.getElementById('naver-maps-script');
            if (script) {
                script.remove();
            }
            setIsMapLoaded(false);
            setMapError(null);
        };
    }, []);

    return(
        <div className="company">
            <SubBanner
                bgImg={bannerImg}
                bgPosition="center bottom"
                title={tabLabels[1]}
                tabs={tabList.map((tab,idx) => ({
                    label: tab.label,
                    url: tab.url,
                    active: activeTab === idx,
                    onClick: () => setActiveTab(idx),
                }))}
                location={[
                    { label: "Company", url: "/company" },
                    { label: tabLabels[1], url: "/company/location", active: true }
                ]}
            />

            <section className="sub_section my-[120px] md:my-[160px] md:mt-[220px] mx-auto">
                <div className="com_txt_box mb-[64px]">
                    <p>{t('sub_section_1.location_title')}</p>
                </div>
                <div className="local_content">
                    <div className="local_left">
                        {mapError ? (
                            <div className="error-message" style={{
                                width: '100%',
                                height: '400px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f8f9fa',
                                color: '#dc3545',
                                border: '1px solid #dee2e6'
                            }}>
                                {mapError}
                            </div>
                        ) : (
                            <div 
                                id="map" 
                                ref={mapElement}
                                style={{ width: '100%', height: '400px' }}
                            />
                        )}
                    </div>
                    <div className="local_right">
                        <ul>
                            {locationInfo.map((item, idx) => (
                                <li key={idx}>
                                    <span className="label">{item.label}</span>
                                    <p>{item.content}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Location;